import React from 'react';
import { useVideoRingDetails, VideoRingUser, VideoRingLevel } from '@/hooks/useVideoRingDetails';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Loader2, CheckCircle2, XCircle, Phone, PhoneMissed, User } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface VideoRingDetailsPanelProps {
  callId: string;
}

function UserStatusBadge({ user }: { user: VideoRingUser }) {
  const { t } = useTranslation();
  
  switch (user.status) {
    case 'accepted':
      return (
        <Badge variant="default" className="bg-green-100 text-green-700 border-0 gap-1">
          <CheckCircle2 className="h-3 w-3" />
          {t('videoRing.accepted', 'Atendeu')}
        </Badge>
      );
    case 'rejected':
      return (
        <Badge variant="outline" className="border-orange-300 text-orange-600 gap-1">
          <XCircle className="h-3 w-3" />
          {t('videoRing.rejected', 'Recusou')}
        </Badge>
      );
    case 'missed':
      return (
        <Badge variant="destructive" className="gap-1">
          <PhoneMissed className="h-3 w-3" />
          {t('videoRing.missed', 'Missed')}
        </Badge>
      );
    case 'ringing':
      return (
        <Badge variant="secondary" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('videoRing.ringing', 'Tocando')}
        </Badge>
      );
    default:
      return null;
  }
}

function LevelSection({ levelData, levelNames }: { levelData: VideoRingLevel; levelNames: string[] }) {
  const levelName = levelNames[levelData.level - 1] || `Nível ${levelData.level}`;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="text-xs font-medium">
          Nível {levelData.level}
        </Badge>
        <span className="text-xs text-muted-foreground">{levelName}</span>
      </div>
      <div className="pl-2 space-y-1.5">
        {levelData.users.map((user) => (
          <div key={user.id} className="flex items-center justify-between gap-2 py-1">
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-xs">{user.initials}</AvatarFallback>
              </Avatar>
              <span className="text-sm">{user.name}</span>
            </div>
            <UserStatusBadge user={user} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function VideoRingDetailsPanel({ callId }: VideoRingDetailsPanelProps) {
  const { t } = useTranslation();
  const { data: details, isLoading } = useVideoRingDetails(callId);
  
  const levelNames = [
    t('videoRing.levelNames.firstResponders', 'Consultores'),
    t('videoRing.levelNames.management', 'Gerência'),
    t('videoRing.levelNames.finalEscalation', 'Escalonamento Final'),
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!details) {
    return null;
  }

  return (
    <div className="border-t pt-3 mt-3 space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <User className="h-4 w-4 text-muted-foreground" />
        <span className="text-muted-foreground">Iniciado por:</span>
        {details.requestedBy ? (
          <div className="flex items-center gap-1.5">
            <Avatar className="h-5 w-5">
              <AvatarFallback className="text-xs">{details.requestedBy.initials}</AvatarFallback>
            </Avatar>
            <span className="font-medium">{details.requestedBy.name}</span>
          </div>
        ) : (
          <span className="text-muted-foreground italic">Desconhecido</span>
        )}
      </div>

      {/* Ring Status */}
      <div className="flex items-center gap-2">
        <Phone className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Status:</span>
        <Badge 
          variant={details.status === 'accepted' ? 'default' : details.status === 'timeout' ? 'destructive' : 'secondary'}
          className={details.status === 'accepted' ? 'bg-green-100 text-green-700 border-0' : ''}
        >
          {details.status === 'accepted' && 'Atendido'}
          {details.status === 'timeout' && 'Ring Out'}
          {details.status === 'ringing' && 'Tocando'}
          {details.status === 'cancelled' && 'Cancelado'}
        </Badge>
      </div>

      {/* Levels */}
      <div className="space-y-3">
        {details.levels.map((level) => (
          <LevelSection key={level.level} levelData={level} levelNames={levelNames} />
        ))}
      </div>

      {/* Accepted By */}
      {details.acceptedBy && (
        <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-950 rounded-md">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <span className="text-sm">Atendido por:</span>
          <Avatar className="h-5 w-5">
            <AvatarFallback className="text-xs bg-green-100 text-green-700">{details.acceptedBy.initials}</AvatarFallback>
          </Avatar>
          <span className="font-medium text-green-700">{details.acceptedBy.name}</span>
        </div>
      )}
    </div>
  );
}
